import {
  AdminLayoutComponent,
  DropdownOption,
  RetractableComponent,
} from "deinestadtliebt-component-library";
import { CurrentPage } from "../../utils/navigation/Navigation.types";
import { useNavLayout } from "../../utils/navigation/NavigationUtils";
import { useTranslation } from "react-i18next";
import { ReactComponent as ImagesIcon } from "../../assets/icons/images.svg";
import { useContext, useEffect, useState } from "react";
import { useAxios } from "../../utils/AxiosUtil";
import { ConfigContext, UserContext } from "../App";
import { fetchUserById } from "../../utils/user/UserUtils";
import {
  createEmptyProvider,
  Provider,
  UserRole,
} from "../../utils/user/User.types";
import "../../styles/ProviderEventsPageStyle.scss";
import { getCalendarWeeks } from "../../utils/time/TimeUtils";
import { CalendarWeeks } from "../../utils/time/Time.types";
import { fetchAllEventsForProvider } from "../../utils/event/EventUtils";
import { EventLocationEdit } from "../../components/eventlocationedit/EventLocationEdit";
import { TicketPricingEdit } from "../../components/ticketpricingedit/TicketPricingEdit";

interface ProviderEventConfigurationPageProps {}

const ProviderEventConfigurationPage: React.FC<
  ProviderEventConfigurationPageProps
> = () => {
  const { t } = useTranslation();
  const axios = useAxios();
  const { user, setUser } = useContext(UserContext);
  const { appConfig } = useContext(ConfigContext);
  const [localProvider, setLocalProvider] = useState<Provider>(
    createEmptyProvider(false)
  );
  const [isLoading, toggleLoading] = useState<boolean>(false);

  const [dropdownCalendarWeeks, setDropdownCalendarWeeks] = useState<
    DropdownOption[]
  >([]);
  const currentCalendarWeeks: CalendarWeeks[] = getCalendarWeeks();

  /**
   * this useEffect creates the needed calendar week entries in dropdown
   */
  useEffect(() => {
    currentCalendarWeeks.forEach((item) =>
      dropdownCalendarWeeks.push({
        label: `${t("providerEventsConfigurationPage.calendarWeeksShort")} ${
          item.name
        } (${new Date(item.startDate).toLocaleDateString("de")} - ${new Date(
          item.endDate
        ).toLocaleDateString("de")})`,
        value: item.name,
      })
    );
    setDropdownCalendarWeeks([...dropdownCalendarWeeks]);
    // eslint-disable-next-line
  }, []);

  /**
   * this useEffect sets the provider by fetching it with the given user-id
   */
  useEffect(() => {
    if (!axios || !user) return;
    fetchUserById(axios, user.id!, UserRole.PROVIDER).then((loadedprovider) => {
      setLocalProvider(loadedprovider as Provider);
      fetchAllEventsForProvider(axios, (loadedprovider as Provider).slugid);
    });
    // eslint-disable-next-line
  }, [axios, user]);

  return (
    <AdminLayoutComponent
      {...useNavLayout(CurrentPage.PROVIDER_DETAIL_CONFIGURATION)}
    >
      <div
        style={{ background: appConfig?.highlightColor }}
        className="default-page-headline"
      >
        <h1>{t("providerEventsConfigurationPage.title")}</h1>
      </div>

      <div
        id="provider-events-configuration-wrapper"
        className="default-page-wrapper"
      >
        <RetractableComponent
          title={t("providerEventsConfigurationPage.location")}
          type="border"
          icon={<ImagesIcon />}
        >
          <EventLocationEdit
            type="event"
            isLoading={isLoading}
            provider={localProvider}
            setProvider={setLocalProvider}
            toggleLoading={toggleLoading}
          />
        </RetractableComponent>

        <RetractableComponent
          title={t("providerEventsConfigurationPage.bookingOffices")}
          type="border"
          icon={<ImagesIcon />}
        >
          <p className="description-text-retractable">
            {t("providerEventsConfigurationPage.bookingOfficesDescription")}
          </p>
          <EventLocationEdit
            type="booking"
            isLoading={isLoading}
            provider={localProvider}
            setProvider={setLocalProvider}
            toggleLoading={toggleLoading}
          />
        </RetractableComponent>

        <RetractableComponent
          title={t("providerEventsConfigurationPage.ticketPricing")}
          type="border"
          icon={<ImagesIcon />}
        >
          <p className="description-text-retractable">
            {t("providerEventsConfigurationPage.ticketPricingDescription")}
          </p>
          <TicketPricingEdit
            setUser={setUser}
            provider={localProvider}
            setProvider={setLocalProvider}
            isLoading={isLoading}
            toggleLoading={toggleLoading}
          />
        </RetractableComponent>
      </div>
    </AdminLayoutComponent>
  );
};

export default ProviderEventConfigurationPage;
